import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Edit2, Save, X, Quote, Image, GripVertical, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to proxy Google Drive images to bypass CORS
const getProxiedImageUrl = (url) => {
  if (!url) return url;
  // Check if it's a Google Drive URL that needs proxying
  if (url.includes('drive.google.com') || url.includes('googleusercontent.com')) {
    return `${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const AdminTravelQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuote, setNewQuote] = useState({
    quote: '',
    author: '',
    image_url: '',
    active: true,
    order: 0,
  });
  const [saving, setSaving] = useState(false);

  const adminAuth = localStorage.getItem('adminAuth');

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/travel-quotes`, {
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (!res.ok) throw new Error('Failed to fetch quotes');
      const data = await res.json();
      setQuotes(data);
    } catch (err) {
      toast.error('Failed to load quotes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const startEdit = (quote) => {
    setEditingId(quote.id);
    setEditForm({ ...quote });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/travel-quotes/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quote: editForm.quote,
          author: editForm.author,
          image_url: editForm.image_url || null,
          active: editForm.active,
          order: parseInt(editForm.order) || 0
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update');
      }
      toast.success('Quote updated successfully');
      setEditingId(null);
      setEditForm({});
      fetchQuotes();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/travel-quotes/${quoteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      toast.success('Quote deleted');
      fetchQuotes();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddQuote = async (e) => {
    e.preventDefault();
    if (!newQuote.quote || !newQuote.author) {
      toast.error('Please fill quote and author fields');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/travel-quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newQuote,
          order: parseInt(newQuote.order) || quotes.length
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add quote');
      }
      toast.success('Quote added successfully');
      setShowAddForm(false);
      setNewQuote({ quote: '', author: '', image_url: '', active: true, order: 0 });
      fetchQuotes();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Travel Quotes</h1>
          <p className="text-gray-600 mt-1">Manage quotes shown during AI itinerary generation</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-quote-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Quote
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900">How Quotes Work</h4>
              <ul className="text-sm text-blue-800 mt-1 space-y-1">
                <li>• Quotes rotate every 30 seconds during itinerary generation</li>
                <li>• Each quote can have an optional image (recommended size: 400x300px)</li>
                <li>• Use the Order field to control the display sequence</li>
                <li>• Inactive quotes will not be shown to users</li>
                <li>• If no quotes are added, default quotes will be used</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add New Quote Form */}
      {showAddForm && (
        <Card className="border-amber-200 bg-amber-50" data-testid="add-quote-form">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Quote className="w-5 h-5" />
              Add New Quote
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddQuote} className="space-y-4">
              <div>
                <Label htmlFor="quote">Quote Text *</Label>
                <textarea
                  id="quote"
                  rows={3}
                  placeholder="The world is a book and those who do not travel read only one page."
                  value={newQuote.quote}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, quote: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  data-testid="new-quote-text"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="author">Author *</Label>
                  <Input
                    id="author"
                    placeholder="Saint Augustine"
                    value={newQuote.author}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, author: e.target.value }))}
                    data-testid="new-quote-author"
                  />
                </div>
                <div>
                  <Label htmlFor="image_url">Image URL (optional)</Label>
                  <Input
                    id="image_url"
                    placeholder="https://example.com/image.jpg"
                    value={newQuote.image_url}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, image_url: e.target.value }))}
                    data-testid="new-quote-image"
                  />
                  <p className="text-xs text-gray-500 mt-1">Recommended: 400x300px</p>
                </div>
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={newQuote.order}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, order: e.target.value }))}
                    data-testid="new-quote-order"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={newQuote.active}
                  onChange={(e) => setNewQuote(prev => ({ ...prev, active: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label htmlFor="active" className="cursor-pointer">Active (show to users)</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-new-quote">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Quote'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} data-testid="cancel-new-quote">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Quotes List */}
      <Card data-testid="quotes-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Quote className="w-5 h-5" />
            All Quotes ({quotes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Quote className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No quotes added yet. Default quotes will be used.</p>
              <Button onClick={() => setShowAddForm(true)} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Add your first quote
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {quotes.map((quote) => (
                <div 
                  key={quote.id} 
                  className={`border rounded-lg p-4 ${quote.active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                  data-testid={`quote-row-${quote.id}`}
                >
                  {editingId === quote.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div>
                        <Label>Quote Text</Label>
                        <textarea
                          rows={2}
                          value={editForm.quote}
                          onChange={(e) => setEditForm(prev => ({ ...prev, quote: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <Label>Author</Label>
                          <Input
                            value={editForm.author}
                            onChange={(e) => setEditForm(prev => ({ ...prev, author: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Image URL</Label>
                          <Input
                            value={editForm.image_url || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, image_url: e.target.value }))}
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <Label>Order</Label>
                          <Input
                            type="number"
                            value={editForm.order}
                            onChange={(e) => setEditForm(prev => ({ ...prev, order: e.target.value }))}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editForm.active}
                              onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Active</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-green-600 hover:bg-green-700">
                          <Save className="w-4 h-4 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start gap-4">
                      <div className="flex items-center text-gray-400">
                        <GripVertical className="w-4 h-4" />
                        <span className="text-xs ml-1">{quote.order}</span>
                      </div>
                      {quote.image_url && (
                        <img 
                          src={getProxiedImageUrl(quote.image_url)} 
                          alt="" 
                          className="w-16 h-12 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 italic">&ldquo;{quote.quote}&rdquo;</p>
                        <p className="text-sm text-amber-600 font-semibold mt-1">— {quote.author}</p>
                        {!quote.active && (
                          <span className="inline-block text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded mt-2">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => startEdit(quote)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(quote.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTravelQuotes;
